import { Maximize2Icon, Minimize2Icon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogPanel, DialogPopup } from "@/components/ui/dialog";
import { BREAKPOINT } from "@/configs/breakpoints";
import { useMediaQuery } from "@/hooks/use-media-query";
import { NAMESPACES } from "@/i18n/constants";
import type { CLOCK_CADENCE } from "@/providers/wall-clock";
import { CrosshairBusProvider } from "./data-viewer-provider";
import { PanelStack } from "./panels/panel-stack";
import { DebugForceRawToggle } from "./resolution/debug-force-raw-toggle";
import { AutoRefreshTicker } from "./time-window/auto-refresh-ticker";
import { RefreshButton } from "./time-window/refresh-button";
import { WindowRangePicker } from "./time-window/window-range-picker";

const EXPANDED_PANEL_HEIGHT_PX = 400;

interface ViewerPanelProps {
    resizable?: boolean;
    controls?: ReactNode;
    baseHeightPx?: number;
    showRefresh: boolean;
    isEmpty?: boolean;
    /** Expand or collapse button, at the end of the toolbar. */
    action?: ReactNode;
}

function ViewerPane({ resizable = true, controls, baseHeightPx, showRefresh, isEmpty, action }: ViewerPanelProps) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "messages" });

    return (
        <CrosshairBusProvider>
            <div className={"flex flex-col gap-[16px]"}>
                <div className={"flex items-center gap-2 border-b pb-[16px] justify-between"}>
                    <div className={"flex items-center gap-2"}>
                        {controls}
                        {import.meta.env.DEV && <DebugForceRawToggle />}
                        <WindowRangePicker />
                    </div>
                    <div className={"flex items-center justify-between gap-1 sm:justify-end"}>
                        {showRefresh && <RefreshButton />}
                        {action}
                    </div>
                </div>
                {isEmpty ? (
                    <div className={"py-8 text-muted-foreground text-sm text-center"}>{t("empty")}</div>
                ) : (
                    <PanelStack resizable={resizable} baseHeightPx={baseHeightPx} />
                )}
            </div>
        </CrosshairBusProvider>
    );
}

interface DataViewerBodyProps {
    autoRefreshCadence?: CLOCK_CADENCE;
    resizable?: boolean;
    controls?: ReactNode;
    baseHeightPx?: number;
    isEmpty?: boolean;
}

export function DataViewerBody({
    autoRefreshCadence,
    resizable,
    controls,
    baseHeightPx,
    isEmpty,
}: DataViewerBodyProps) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "expand" });
    const [expanded, setExpanded] = useState(false);
    const isMobile = useMediaQuery(BREAKPOINT.SM);

    const showRefresh = autoRefreshCadence != null;

    return (
        <>
            {/* Both views share one store, so one ticker up here instead of a timer per view. */}
            {autoRefreshCadence != null && <AutoRefreshTicker cadence={autoRefreshCadence} />}
            <ViewerPane
                action={
                    isMobile ? undefined : (
                        <Button
                            aria-label={t("open")}
                            onClick={() => setExpanded(true)}
                            size={"icon"}
                            tooltip={t("open")}
                            variant={"outline"}
                        >
                            <Maximize2Icon />
                        </Button>
                    )
                }
                baseHeightPx={baseHeightPx}
                controls={controls}
                isEmpty={isEmpty}
                resizable={resizable}
                showRefresh={showRefresh}
            />
            <Dialog open={expanded} onOpenChange={setExpanded}>
                <DialogPopup
                    aria-label={t("title")}
                    className={"sm:max-w-[1200px]"}
                    presentation={"fullscreen"}
                    showCloseButton={false}
                >
                    {/* No DialogHeader here, so put back the top padding the panel drops when there is one. */}
                    <DialogPanel className={"pt-4"}>
                        <ViewerPane
                            action={
                                <DialogClose
                                    aria-label={t("close")}
                                    render={<Button size={"icon"} tooltip={t("close")} variant={"outline"} />}
                                >
                                    <Minimize2Icon />
                                </DialogClose>
                            }
                            baseHeightPx={EXPANDED_PANEL_HEIGHT_PX}
                            controls={controls}
                            isEmpty={isEmpty}
                            resizable={resizable}
                            showRefresh={showRefresh}
                        />
                    </DialogPanel>
                </DialogPopup>
            </Dialog>
        </>
    );
}
